import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { globalAppCache } from "../utils/cache";
import { STATUS_COLOR } from "../constants/tableStatus";
import { useAuth } from "./authentication/AuthContext";
import QuickStatsStrip from "./QuickStatsStrip";
import {
  Plus,
  ShoppingBag,
  Layers,
  RefreshCw
} from "lucide-react";

// Modern colors for status badges in the legend and UI (soft, polished theme)
const STATUS_LABEL_MAP = {
  FREE: { label: "Free / Vacant", color: "from-emerald-500 to-teal-600 shadow-emerald-200" },
  ORDER_PLACED: { label: "Active Order", color: "from-blue-500 to-indigo-600 shadow-blue-200" },
  PREPARING: { label: "KOT Preparing", color: "from-amber-500 to-orange-600 shadow-amber-200" },
  BILL_PRINTED: { label: "Bill Printed", color: "from-fuchsia-500 to-purple-600 shadow-fuchsia-200" },
  CLOSED: { label: "Payment Settled", color: "from-zinc-600 to-zinc-700 shadow-zinc-200" },
};

const SelectPlace = () => {
  const [layout, setLayout] = useState(null);
  const [activeOrders, setActiveOrders] = useState({});
  const [parcelOrders, setParcelOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const { restaurantId, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (!restaurantId) return;
    loadData();

    const handleSyncComplete = () => {
      loadData();
    };

    window.addEventListener("sync-complete", handleSyncComplete);
    return () => {
      window.removeEventListener("sync-complete", handleSyncComplete);
    };
  }, [authLoading, restaurantId]);

  const loadData = async (silent = false) => {
    // Optimistic Layout Load
    if (globalAppCache.layoutCache.data) {
      setLayout(globalAppCache.layoutCache.data);
    } else {
      if (!silent) setLoading(true);
    }

    if (silent) setRefreshing(true);

    // Layout (floors/tables) and active orders (incl. takeaway/parcels) are
    // independent — a restaurant with no layout configured yet should still
    // see its takeaway/parcel tickets, so these must not fail together.
    const [layoutResult, ordersResult] = await Promise.allSettled([
      api.get("/layout"),
      api.get("/orders/active"),
    ]);

    if (layoutResult.status === "fulfilled") {
      globalAppCache.layoutCache.data = layoutResult.value.data;
      globalAppCache.layoutCache.timestamp = Date.now();
      setLayout(layoutResult.value.data);
    } else {
      // 404 (no layout set up yet) is expected — treat as "no layout"
      setLayout(null);
    }

    if (ordersResult.status === "fulfilled") {
      setActiveOrders(ordersResult.value.data.tables || {});
      setParcelOrders(ordersResult.value.data.parcels || []);
    } else {
      console.error("Failed to load active orders", ordersResult.reason);
    }

    setLoading(false);
    setRefreshing(false);
  };

  const handleParcel = async () => {
    try {
      const res = await api.post("/orders/parcel");
      navigate(`/home?orderId=${res.data._id}`);
    } catch (err) {
      console.error("Failed to create parcel order", err);
    }
  };

  const getOrder = (floorName, sectionName, unitNumber, roomType) =>
    activeOrders[
      `${floorName}-${sectionName}-${roomType || "NA"}-${unitNumber}`
    ];

  const handleClick = async (floor, section, unitNumber) => {
    try {
      const res = await api.post("/orders/select-table", {
        floorName: floor.floorName,
        sectionName: section.name,
        sectionType: section.type,
        unitNumber,
        roomType: section.roomType || null,
      });

      navigate(`/home?orderId=${res.data._id}`);
    } catch (err) {
      console.error("Failed to select table", err);
    }
  };

  // 🌟 Loading / Shimmer Skeletons
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-8 mt-14">
        <div className="max-w-7xl mx-auto space-y-8 animate-pulse">
          {/* Header Shimmer */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="space-y-2">
              <div className="h-8 w-48 bg-slate-200 rounded-xl"></div>
              <div className="h-4 w-64 bg-slate-200 rounded-lg"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-6 w-24 bg-slate-200 rounded-full"></div>
              <div className="h-6 w-24 bg-slate-200 rounded-full"></div>
              <div className="h-6 w-24 bg-slate-200 rounded-full"></div>
            </div>
          </div>

          {/* Parcel Section Shimmer */}
          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <div className="h-6 w-36 bg-slate-200 rounded-lg"></div>
              <div className="h-10 w-28 bg-slate-200 rounded-xl"></div>
            </div>
            <div className="flex gap-3">
              <div className="h-12 w-24 bg-slate-200 rounded-xl"></div>
              <div className="h-12 w-24 bg-slate-200 rounded-xl"></div>
            </div>
          </div>

          {/* Floor Shimmer */}
          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-6">
            <div className="h-6 w-32 bg-slate-200 rounded-lg"></div>
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="h-5 w-24 bg-slate-200 rounded-lg"></div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-4">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="h-16 bg-slate-200 rounded-2xl"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 mt-14 pb-12">
      <QuickStatsStrip />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Top Header — compact */}
        <div className="flex flex-wrap gap-3 justify-between items-center bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-base font-bold text-slate-800 font-serif whitespace-nowrap">Select Table / Room</h1>
            <span className="hidden sm:inline text-xs text-slate-400 truncate">Tap a table to view or start billing</span>
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="p-1 hover:bg-slate-100 rounded-md transition disabled:opacity-50 shrink-0"
              title="Refresh occupancy status"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* Visual Indicators / Status Legend */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-semibold">
            {Object.entries(STATUS_LABEL_MAP).map(([status, details]) => (
              <div key={status} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${details.color}`}></span>
                <span className="text-slate-500">{details.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Parcel / Takeaway Card */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
          <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-teal-50 rounded-xl text-teal-600">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800 font-serif">Takeaway / Parcels</h2>
                <p className="text-slate-400 text-xs font-semibold">Active direct takeaway/counter billing tickets</p>
              </div>
            </div>

            <button
              onClick={handleParcel}
              className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 active:scale-95 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow-md shadow-teal-100 transition-all duration-150"
            >
              <Plus className="w-4 h-4" /> New Takeaway
            </button>
          </div>

          {parcelOrders.length === 0 ? (
            <div className="text-center py-6 text-slate-400 font-medium text-xs">
              No active takeaway tickets at the moment. Click "New Takeaway" to create one.
            </div>
          ) : (
            <div className="flex gap-3 flex-wrap">
              {parcelOrders.map((p) => {
                const orderStatus = p.status || "FREE";
                const badgeColor = STATUS_COLOR[orderStatus] || STATUS_COLOR.FREE;
                return (
                  <div
                    key={p.orderId}
                    onClick={() => navigate(`/home?orderId=${p.orderId}`)}
                    className={`group px-4 py-3 rounded-2xl text-white font-bold text-sm cursor-pointer shadow-sm hover:shadow-md transition-all duration-200 transform hover:-translate-y-0.5 flex flex-col gap-0.5 min-w-[110px] ${badgeColor}`}
                  >
                    <div className="flex items-center gap-2">
                      <span>🛍️ Parcel #{p.orderNumber}</span>
                      {p.source === "QR" && (
                        <span className="text-[9px] bg-yellow-400 text-black px-1.5 py-0.5 rounded-md font-extrabold uppercase">
                          QR
                        </span>
                      )}
                    </div>
                    {p.customerName && (
                      <span className="text-[10px] font-semibold opacity-90 truncate max-w-[120px]">
                        👤 {p.customerName}
                      </span>
                    )}
                    {p.subtotal > 0 && (
                      <span className="text-xs font-extrabold opacity-90 bg-black/20 rounded-md px-1.5 py-0.5 w-fit">
                        ₹{p.subtotal.toLocaleString("en-IN")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Floors & Seating Configurations */}
        {!layout ? (
          <div className="bg-white shadow-sm rounded-3xl p-8 md:p-12 text-center w-full border border-slate-100/80">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 flex items-center justify-center rounded-3xl bg-indigo-50 text-indigo-600 shadow-inner">
                <Layers className="w-10 h-10" />
              </div>
            </div>
            <h1 className="text-2xl font-extrabold text-slate-800 mb-2 font-serif">
              No Seating Layout Found
            </h1>
            <p className="text-slate-500 mb-8 text-sm font-medium leading-relaxed max-w-sm mx-auto">
              You haven't structured floors, dining tables, or guest rooms yet. Set up your layout configuration to enable table/room billing.
            </p>
            <button
              onClick={() => navigate("/structure")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white px-8 py-3.5 rounded-2xl font-bold shadow-lg shadow-indigo-200 transition-all duration-150"
            >
              <Plus className="w-5 h-5" /> Setup Restaurant Layout
            </button>
          </div>
        ) : (
        <div className="space-y-8">
          {layout.floors.map((floor) => (
            <div
              key={floor.floorName}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-6"
            >
              {/* Floor Header */}
              <div className="flex items-center gap-2 border-b border-slate-50 pb-4">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                  <Layers className="w-5 h-5" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 font-serif">
                  {floor.floorName}
                </h2>
              </div>

              {floor.sections.map((section) => (
                <div key={section.name} className="space-y-3">
                  {/* Section Title & Type Badges */}
                  <div className="flex items-center gap-2.5">
                    <span className="text-sm font-bold text-slate-500 font-sans tracking-wide uppercase">
                      {section.name}
                    </span>
                    {section.type === "ROOM" && section.roomType && (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 text-[10px] font-bold rounded-full border ${
                          section.roomType === "AC"
                            ? "bg-blue-50 border-blue-200 text-blue-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                        }`}
                      >
                        {section.roomType === "AC" ? "❄️ AC Room" : "🌤 Non-AC Room"}
                      </span>
                    )}
                  </div>

                  {/* Units Seating Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4">
                    {Array.from({ length: section.totalUnits }).map((_, i) => {
                      const unitNumber = i + 1;
                      const order = getOrder(
                        floor.floorName,
                        section.name,
                        unitNumber,
                        section.roomType,
                      );
                      const status = order?.status || "FREE";
                      const colorClass = STATUS_COLOR[status] || STATUS_COLOR.FREE;

                      const isOccupied = status !== "FREE";
                      return (
                        <div
                          key={unitNumber}
                          onClick={() => handleClick(floor, section, unitNumber)}
                          className={`relative rounded-2xl flex flex-col items-center justify-center
                            text-white font-extrabold text-sm cursor-pointer border border-transparent
                            transition-all duration-200 transform hover:scale-[1.03] hover:shadow-lg
                            ${isOccupied ? "h-24 pb-3" : "h-20"}
                            ${colorClass}`}
                        >
                          {/* Ticket Number Badge */}
                          {order?.orderNumber && isOccupied && (
                            <span className="absolute top-1.5 left-1.5 text-[9px] bg-black/50 text-white font-extrabold px-1.5 py-0.5 rounded-md">
                              #{order.orderNumber}
                            </span>
                          )}

                          {/* Order Source Tag */}
                          {order?.source === "QR" && (
                            <span className="absolute top-1.5 right-1.5 text-[9px] bg-yellow-400 text-black font-extrabold px-1.5 py-0.5 rounded-md animate-pulse">
                              QR
                            </span>
                          )}

                          {/* Unit Label + Number */}
                          <span className="text-[10px] uppercase opacity-80 tracking-wider font-semibold leading-none">
                            {section.type === "TABLE" ? "Table" : "Room"}
                          </span>
                          <span className="text-xl font-black leading-tight">
                            {unitNumber}
                          </span>

                          {/* Customer name if available */}
                          {isOccupied && order?.customerName && (
                            <span className="text-[9px] font-bold text-white/90 max-w-[80px] truncate leading-none px-1">
                              {order.customerName}
                            </span>
                          )}

                          {/* Amount — shown prominently when occupied */}
                          {isOccupied && order?.subtotal > 0 && (
                            <span className="text-[11px] font-extrabold bg-white text-slate-800 rounded-lg px-2 py-0.5 mt-0.5 leading-none shadow-sm">
                              ₹{order.subtotal.toLocaleString("en-IN")}
                            </span>
                          )}

                          {/* Status strip at bottom of card */}
                          {isOccupied && (
                            <span className="absolute bottom-0 left-0 right-0 text-[8px] bg-black/40 font-extrabold uppercase py-1 rounded-b-2xl text-center tracking-wide">
                              {status.replace(/_/g, " ")}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        )}

      </div>
    </div>
  );
};

export default SelectPlace;
